// frontend/src/services/overtimeManagement.service.ts
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { createOvertimeService, Employee } from './createOvertime.service';

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  duplicates: Array<{
    employeeName: string;
    employeeId: string;
    projectName: string;
    requesterName: string;
    requestId: string;
    status: string;
    overtimeDate: Date;
  }>;
  message?: string;
}

export interface AddEmployeesResult {
  success: boolean;
  message: string;
  requestId?: string;
  addedCount?: number;
  skippedCount?: number;
  skippedEmployees?: string[];
}

export interface OvertimeRequestSummary {
  requestId: string;
  projectName: string;
  projectCode: string;
  overtimeDate: Date;
  startTime: Date;
  endTime: Date;
  status: string;
  employees: Array<{
    id: string;
    name: string;
    designation: string;
    department: string;
  }>;
  canAddMore: boolean;
  requesterName: string;
  approverName: string;
}

class OvertimeManagementService {
  /**
   * Check for duplicate overtime applications
   */
  async checkDuplicateOvertime(
    employeeIds: string[],
    overtimeDate: Date,
    projectCode: string,
    excludeRequestId?: string
  ): Promise<DuplicateCheckResult> {
    try {
      console.log('🔍 Checking for duplicate overtime applications...');
      
      const dateStr = overtimeDate.toISOString().split('T')[0];
      const duplicates: DuplicateCheckResult['duplicates'] = [];

      // Query overtime requests for the same date and project
      const overtimeQuery = query(
        collection(db, 'overtime_requests'),
        where('projectCode', '==', projectCode),
        where('status', 'in', ['pending', 'approved'])
      );

      const snapshot = await getDocs(overtimeQuery);

      for (const requestDoc of snapshot.docs) {
        if (excludeRequestId && requestDoc.id === excludeRequestId) {
          continue; // Skip the request being edited
        }

        const data = requestDoc.data();
        const requestDate = data.overtimeDate?.toDate();
        
        if (!requestDate) continue;
        
        const requestDateStr = requestDate.toISOString().split('T')[0];
        
        // Check if dates match
        if (requestDateStr === dateStr) {
          // Check for employee overlap
          const requestEmployeeIds = data.employeeIds || [];
          const overlappingEmployees = employeeIds.filter(empId => 
            requestEmployeeIds.includes(empId)
          );

          if (overlappingEmployees.length > 0) {
            // Get employee details
            const employees = await createOvertimeService.getEligibleEmployees();
            
            for (const empId of overlappingEmployees) {
              const employee = employees.find(e => e.id === empId);
              
              duplicates.push({
                employeeName: employee?.name || empId,
                employeeId: empId,
                projectName: data.projectName || 'Unknown Project',
                requesterName: data.requesterName || 'Unknown Requester',
                requestId: requestDoc.id,
                status: data.status,
                overtimeDate: requestDate
              });
            }
          }
        }
      }

      if (duplicates.length > 0) {
        const employeeNames = [...new Set(duplicates.map(d => d.employeeName))].join(', ');
        return {
          isDuplicate: true,
          duplicates,
          message: `Already applied for: ${employeeNames} in ${duplicates[0].projectName} on ${dateStr}`
        };
      }

      return {
        isDuplicate: false,
        duplicates: [],
        message: 'No duplicates found'
      };

    } catch (error) {
      console.error('❌ Error checking duplicates:', error);
      return {
        isDuplicate: false,
        duplicates: [],
        message: 'Error checking duplicates'
      };
    }
  }

  /**
   * Get overtime request summary by ID
   */
  async getOvertimeRequestSummary(requestId: string): Promise<OvertimeRequestSummary | null> {
    try {
      console.log('📋 Loading overtime request summary:', requestId);
      
      const requestRef = doc(db, 'overtime_requests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        console.log('❌ Request not found');
        return null;
      }

      const data = requestDoc.data();
      const employees = await createOvertimeService.getEligibleEmployees();

      const employeeDetails = (data.employeeIds || []).map((empId: string) => {
        const employee = employees.find(e => e.id === empId);
        return {
          id: empId,
          name: employee?.name || 'Unknown',
          designation: employee?.designation || 'N/A',
          department: employee?.department || 'N/A'
        };
      });

      return {
        requestId: requestDoc.id,
        projectName: data.projectName || '',
        projectCode: data.projectCode || '',
        overtimeDate: data.overtimeDate?.toDate() || new Date(),
        startTime: data.startTime?.toDate() || new Date(),
        endTime: data.endTime?.toDate() || new Date(),
        status: data.status || 'pending',
        employees: employeeDetails,
        canAddMore: data.status === 'pending',
        requesterName: data.requesterName || '',
        approverName: data.approverName || ''
      };

    } catch (error) {
      console.error('❌ Error loading request summary:', error);
      return null;
    }
  }

  /**
   * Search employees by PIN or Name for adding to existing request
   */
  async searchEmployeesForAdding(
    searchTerm: string,
    overtimeDate: Date,
    existingEmployeeIds: string[]
  ): Promise<Employee[]> {
    try {
      console.log('🔍 Searching employees for:', searchTerm);
      
      // Get all eligible employees with attendance for the date
      const allEmployees = await createOvertimeService.getEligibleEmployeesForDate(overtimeDate);
      
      const searchLower = searchTerm.toLowerCase().trim();
      
      // Filter out already selected employees
      const availableEmployees = allEmployees.filter(emp =>
          !existingEmployeeIds.includes(emp.id)  // ✅ ONLY CHECK IF NOT ALREADY SELECTED
        );

      // Search by PIN or Name
      const filtered = availableEmployees.filter(emp => {
        const nameMatch = emp.name.toLowerCase().includes(searchLower);
        const pinMatch = (emp.pin || '').toLowerCase().includes(searchLower);
        const empNumberMatch = (emp.employeeNumber || '').toLowerCase().includes(searchLower);
        const idMatch = emp.id.toLowerCase().includes(searchLower);
        
        return nameMatch || pinMatch || empNumberMatch || idMatch;
      });

      console.log(`✅ Found ${filtered.length} matching employees`);
      return filtered;

    } catch (error) {
      console.error('❌ Error searching employees:', error);
      return [];
    }
  }

  /**
   * Add more employees to PENDING request
   */
  async addEmployeesToPendingRequest(
    requestId: string,
    newEmployeeIds: string[],
    requesterId: string,
    requesterName: string
  ): Promise<AddEmployeesResult> {
    try {
      console.log('➕ Adding employees to pending request:', requestId);

      // Get current request
      const requestRef = doc(db, 'overtime_requests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        return {
          success: false,
          message: 'Overtime request not found'
        };
      }

      const data = requestDoc.data();

      // Verify status is pending
      if (data.status !== 'pending') {
        return {
          success: false,
          message: `Cannot add employees to ${data.status} request. Create a new request instead.`
        };
      }

      // Verify requester
      if (data.requesterId !== requesterId) {
        return {
          success: false,
          message: 'You can only add employees to your own requests'
        };
      }

      const currentEmployeeIds = data.employeeIds || [];
      
      // Filter out duplicates
      const employeesToAdd = newEmployeeIds.filter(empId => 
        !currentEmployeeIds.includes(empId)
      );

      if (employeesToAdd.length === 0) {
        return {
          success: false,
          message: 'All selected employees are already in this request',
          skippedCount: newEmployeeIds.length
        };
      }

      // Check duplicates across other requests
      const duplicateCheck = await this.checkDuplicateOvertime(
        employeesToAdd,
        data.overtimeDate?.toDate() || new Date(),
        data.projectCode,
        requestId // Exclude current request
      );

      if (duplicateCheck.isDuplicate) {
        return {
          success: false,
          message: duplicateCheck.message || 'Some employees already have overtime for this date'
        };
      }

      // Get employee details
      const employees = await createOvertimeService.getEligibleEmployees();
      const newEmployeeDetails = employeesToAdd.map(empId => {
        const emp = employees.find(e => e.id === empId);
        return {
          id: empId,
          name: emp?.name || 'Unknown',
          designation: emp?.designation || 'N/A',
          department: emp?.department || 'N/A',
          employeeNumber: emp?.employeeNumber || empId
        };
      });

      // Update request
      await updateDoc(requestRef, {
        employeeIds: arrayUnion(...employeesToAdd),
        employeeDetails: arrayUnion(...newEmployeeDetails),
        totalEmployees: currentEmployeeIds.length + employeesToAdd.length,
        updatedAt: serverTimestamp(),
        lastModifiedBy: requesterId,
        lastModifiedByName: requesterName,
        modificationHistory: arrayUnion({
          action: 'employees_added',
          addedEmployees: employeesToAdd,
          addedCount: employeesToAdd.length,
          modifiedBy: requesterId,
          modifiedByName: requesterName,
          timestamp: serverTimestamp()
        })
      });

      console.log(`✅ Added ${employeesToAdd.length} employees to request`);

      return {
        success: true,
        message: `Successfully added ${employeesToAdd.length} employee(s) to the overtime request`,
        requestId,
        addedCount: employeesToAdd.length,
        skippedCount: newEmployeeIds.length - employeesToAdd.length
      };

    } catch (error) {
      console.error('❌ Error adding employees to pending request:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to add employees'
      };
    }
  }

  /**
   * Create new request for APPROVED overtime (add more employees)
   */
  async createAdditionalRequestForApproved(
    originalRequestId: string,
    newEmployeeIds: string[],
    requesterId: string,
    requesterName: string
  ): Promise<AddEmployeesResult> {
    try {
      console.log('📝 Creating new request for approved overtime:', originalRequestId);

      // Get original request
      const originalRef = doc(db, 'overtime_requests', originalRequestId);
      const originalDoc = await getDoc(originalRef);

      if (!originalDoc.exists()) {
        return {
          success: false,
          message: 'Original overtime request not found'
        };
      }

      const originalData = originalDoc.data();

      // Check for duplicates
      const duplicateCheck = await this.checkDuplicateOvertime(
        newEmployeeIds,
        originalData.overtimeDate?.toDate() || new Date(),
        originalData.projectCode
      );

      if (duplicateCheck.isDuplicate) {
        return {
          success: false,
          message: duplicateCheck.message || 'Some employees already have overtime for this date'
        };
      }

      // Get employee details
      const employees = await createOvertimeService.getEligibleEmployees();
      const employeeDetails = newEmployeeIds.map(empId => {
        const emp = employees.find(e => e.id === empId);
        return {
          id: empId,
          name: emp?.name || 'Unknown',
          designation: emp?.designation || 'N/A',
          department: emp?.department || 'N/A',
          employeeNumber: emp?.employeeNumber || empId
        };
      });

      // Calculate duration
      const startTime = originalData.startTime?.toDate() || new Date();
      const endTime = originalData.endTime?.toDate() || new Date();
      const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

      // Create new request
      const newRequestData = {
        // Copy from original
        projectName: originalData.projectName,
        projectCode: originalData.projectCode,
        startTime: originalData.startTime,
        endTime: originalData.endTime,
        overtimeDate: originalData.overtimeDate,
        approverEmpId: originalData.approverEmpId,
        approverName: originalData.approverName,
        
        // New data
        employeeIds: newEmployeeIds,
        employeeDetails: employeeDetails,
        requesterId: requesterId,
        requesterName: requesterName,
        
        // Status and metadata
        status: 'pending',
        requestTime: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Tracking
        totalEmployees: newEmployeeIds.length,
        totalHours: durationHours,
        isActive: true,
        version: 1,
        
        // Link to original
        linkedToRequest: originalRequestId,
        linkedRequestType: 'additional_employees',
        
        // Multi-project structure
        projects: [{
          projectName: originalData.projectName,
          projectCode: originalData.projectCode,
          startTime: originalData.startTime,
          endTime: originalData.endTime,
          employeeIds: newEmployeeIds,
          duration: durationHours,
          overtimeDate: originalData.overtimeDate
        }],
        
        metadata: {
          source: 'web_portal_additional',
          originalRequestId: originalRequestId,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          attendanceValidated: true,
          overtimeDateString: (originalData.overtimeDate?.toDate() || new Date()).toISOString().split('T')[0]
        }
      };

      const docRef = await addDoc(collection(db, 'overtime_requests'), newRequestData);

      console.log('✅ Created new request for additional employees:', docRef.id);

      // Send notifications
      await this.sendNotificationsForAdditional(
        docRef.id,
        originalData.approverEmpId,
        originalData.approverName,
        requesterId,
        requesterName,
        originalData.projectName,
        newEmployeeIds.length,
        originalData.overtimeDate?.toDate() || new Date(),
        originalRequestId
      );

      return {
        success: true,
        message: `Created new overtime request for ${newEmployeeIds.length} additional employee(s)`,
        requestId: docRef.id,
        addedCount: newEmployeeIds.length
      };

    } catch (error) {
      console.error('❌ Error creating additional request:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create additional request'
      };
    }
  }

  /**
   * Send notifications for additional employees
   */
  private async sendNotificationsForAdditional(
    requestId: string,
    approverId: string,
    approverName: string,
    requesterId: string,
    requesterName: string,
    projectName: string,
    employeeCount: number,
    overtimeDate: Date,
    originalRequestId: string
  ): Promise<void> {
    try {
      const notificationsRef = collection(db, 'notifications');
      const dateString = overtimeDate.toLocaleDateString();
      
      // Notification for approver
      await addDoc(notificationsRef, {
        type: 'overtime_request_additional',
        targetUserId: approverId,
        title: 'Additional Overtime Request',
        body: `${requesterName} added ${employeeCount} more employees to ${projectName} overtime on ${dateString}`,
        data: {
          requestId: requestId,
          originalRequestId: originalRequestId,
          projectName: projectName,
          requesterName: requesterName,
          employeeCount: employeeCount.toString(),
          overtimeDate: dateString,
          type: 'additional'
        },
        timestamp: serverTimestamp(),
        processed: false
      });

      // Confirmation for requester
      await addDoc(notificationsRef, {
        type: 'overtime_request_submitted',
        targetUserId: requesterId,
        title: 'Additional Overtime Request Submitted',
        body: `Your additional overtime request for ${employeeCount} employees in ${projectName} on ${dateString} has been sent to ${approverName}`,
        data: {
          requestId: requestId,
          originalRequestId: originalRequestId,
          projectName: projectName,
          approverName: approverName,
          overtimeDate: dateString,
          type: 'additional'
        },
        timestamp: serverTimestamp(),
        processed: false
      });

    } catch (error) {
      console.error('❌ Error sending notifications:', error);
    }
  }

  /**
   * Get recently created request by requester
   */
  async getRecentlyCreatedRequest(
    requesterId: string,
    withinMinutes: number = 5
  ): Promise<OvertimeRequestSummary | null> {
    try {
      const cutoffTime = new Date(Date.now() - withinMinutes * 60 * 1000);
      
      const recentQuery = query(
        collection(db, 'overtime_requests'),
        where('requesterId', '==', requesterId),
        where('status', '==', 'pending')
      );

      const snapshot = await getDocs(recentQuery);
      
      let mostRecent: any = null;
      let mostRecentTime = cutoffTime;

      for (const doc of snapshot.docs) {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        
        if (createdAt && createdAt > mostRecentTime) {
          mostRecent = { id: doc.id, ...data };
          mostRecentTime = createdAt;
        }
      }

      if (mostRecent) {
        return await this.getOvertimeRequestSummary(mostRecent.id);
      }

      return null;

    } catch (error) {
      console.error('❌ Error getting recent request:', error);
      return null;
    }
  }
}

export const overtimeManagementService = new OvertimeManagementService();